import React, { useState } from "react";

import { motion } from "framer-motion";

import { MapPin, Clock3, Mail } from "lucide-react";

import { FaWhatsapp } from "react-icons/fa6";

import { useNavigate } from "react-router-dom";

import JobApplicationModal from "./JobApplicationModal";

import WhatsAppApplicationModal from "./WhatsAppApplicationModal";

const JobCard = ({ job }) => {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isWhatsappModalOpen, setIsWhatsappModalOpen] = useState(false);

  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate(`/career/${job.id}`);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 35,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        viewport={{
          once: true,
        }}
        transition={{
          duration: 0.5,
        }}
        whileHover={{
          y: -6,
        }}
        className="
          group
          relative
          flex
          h-full
          flex-col
          overflow-hidden
          rounded-2xl
          border
          theme-border
          theme-card
          transition-all
          duration-500
          hover:brand-border
          font-saira
        "
      >
        {/* Hover Glow */}
        <div
          className="
            pointer-events-none
            absolute
            inset-0
            bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.12),transparent_65%)]
            opacity-0
            transition-opacity
            duration-500
            group-hover:opacity-100
          "
        />

        <div
          className="
            relative
            flex
            h-full
            flex-col
            p-4
            sm:p-5
            lg:p-6
          "
        >
          {/* ===================== */}
          {/* CLICKABLE CONTENT */}
          {/* ===================== */}

          <div
            onClick={handleNavigate}
            className="cursor-pointer"
          >
            {/* Department */}
            <div className="flex items-center justify-between">
              <span
                className="
                  rounded-full
                  border
                  border-orange-500/20
                  bg-orange-500/10
                  px-3
                  py-1
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-wider
                  brand-color
                  sm:text-xs
                "
              >
                {job.department}
              </span>
            </div>

            {/* Title */}
            <motion.h3
              initial={{
                opacity: 0,
                y: 12,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.15,
              }}
              className="
                mt-4
                line-clamp-2
                text-lg
                font-semibold
                leading-snug
                tracking-tight
                theme-text-primary
                sm:text-xl
              "
            >
              {job.title}
            </motion.h3>

            {/* Description */}
            <motion.p
              initial={{
                opacity: 0,
              }}
              whileInView={{
                opacity: 1,
              }}
              transition={{
                delay: 0.25,
              }}
              className="
                theme-text-muted
                mt-3
                line-clamp-2
                min-h-[48px]
                text-sm
                leading-6
              "
            >
              {job.description}
            </motion.p>

            {/* Job Info */}
            <div className="mt-8 space-y-2.5">
              {/* Location */}
              <div
                className="
                  theme-text-muted
                  flex
                  items-center
                  gap-2.5
                  text-sm
                "
              >
                <MapPin
                  size={16}
                  className="
                    shrink-0
                    brand-color
                  "
                />

                <span className="line-clamp-1">
                  {job.location}
                </span>
              </div>

              {/* Type + Experience */}
              <div
                className="
                  theme-text-muted
                  flex
                  flex-wrap
                  items-center
                  gap-2
                  text-sm
                "
              >
                <Clock3
                  size={16}
                  className="
                    shrink-0
                    brand-color
                  "
                />

                <span>{job.type}</span>

                <span className="theme-text-muted opacity-50">
                  •
                </span>

                <span>{job.experience}</span>
              </div>
            </div>

            {/* Skills */}
            <div className="mt-4 flex flex-wrap gap-2">
              {job.skills.slice(0, 3).map((skill, index) => (
                <motion.span
                  key={index}
                  whileHover={{
                    scale: 1.05,
                  }}
                  className="
                    rounded-full
                    border
                    theme-border
                    theme-card-secondary
                    theme-text-secondary
                    px-2.5
                    py-1
                    text-[11px]
                    font-medium
                    tracking-wide
                    transition-all
                    duration-300
                    hover:brand-border
                    hover:brand-color
                    sm:text-xs
                  "
                >
                  {skill}
                </motion.span>
              ))}

              {job.skills.length > 3 && (
                <span
                  className="
                    rounded-full
                    border
                    border-orange-500/20
                    bg-orange-500/10
                    px-2.5
                    py-1
                    text-[11px]
                    font-semibold
                    brand-color
                    sm:text-xs
                  "
                >
                  +{job.skills.length - 3} More
                </span>
              )}
            </div>
          </div>

          {/* Push buttons to bottom */}
          <div className="flex-grow" />

          {/* ===================== */}
          {/* BUTTONS */}
          {/* ===================== */}

          <div className="mt-5 grid grid-cols-2 gap-2">
            {/* WhatsApp */}
            <motion.button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsWhatsappModalOpen(true);
              }}
              whileHover={{
                scale: 1.03,
              }}
              whileTap={{
                scale: 0.97,
              }}
              className="
                brand-bg
                flex
                h-10
                items-center
                justify-center
                gap-2
                rounded-lg
                px-3
                text-xs
                font-medium
                text-white
                transition-all
                duration-300
                hover:bg-[var(--brand-hover)]
                sm:h-11
                sm:text-sm
              "
            >
              <FaWhatsapp size={16} />

              <span>WhatsApp</span>
            </motion.button>

            {/* Email */}
            <motion.button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsEmailModalOpen(true);
              }}
              whileHover={{
                scale: 1.03,
              }}
              whileTap={{
                scale: 0.97,
              }}
              className="
                brand-border
                brand-color
                flex
                h-10
                items-center
                justify-center
                gap-2
                rounded-lg
                border
                bg-transparent
                px-3
                text-xs
                font-medium
                transition-all
                duration-300
                hover:brand-bg
                hover:text-white
                sm:h-11
                sm:text-sm
              "
            >
              <Mail size={16} />

              <span>Email</span>
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Email Modal */}
      <JobApplicationModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        job={job}
      />

      {/* WhatsApp Modal */}
      <WhatsAppApplicationModal
        isOpen={isWhatsappModalOpen}
        onClose={() => setIsWhatsappModalOpen(false)}
        job={job}
      />
    </>
  );
};

export default JobCard;